import type { FacilityStorageLocation } from '../types/storage-locations.ts';
import type { TierIIReportFormEntry } from '../types/reporting.ts';
import type { SiteMapFeatureCollection } from '../../tri-shared/types/db.ts';

export type SiteMapStorageLocation = FacilityStorageLocation & {
  contents: { key: string; kind: 'Product' | 'Chemical'; name: string; vessels: string[]; chemicals: string[] }[];
};

export function siteMapStorageLocations(
  locations: FacilityStorageLocation[],
  entries: TierIIReportFormEntry[],
): SiteMapStorageLocation[] {
  return locations.map((location) => ({
    ...location,
    contents: entries.flatMap((entry) => {
      const rows = entry.vessels.filter((v) => v.storageLocationID === location.locationID);
      if (!rows.length) return [];
      return [
        {
          key: entry.basis === 'mixture' ? `product:${entry.productID}` : `chemical:${entry.casOrCategory}`,
          kind: entry.basis === 'mixture' ? ('Product' as const) : ('Chemical' as const),
          name: entry.basis === 'mixture' ? entry.productName : entry.chemicalName,
          vessels: [...new Set(rows.map((v) => v.vesselName.trim()).filter((name) => name !== ''))],
          chemicals:
            entry.basis === 'mixture'
              ? [...new Set([...entry.ehsChemicals, ...entry.nonEhsChemicals].map((c) => c.name))]
              : [],
        },
      ];
    }),
  }));
}

/** Preserve geometry and styling while resolving the current shared name. */
export function resolveSiteMapLocationNames(
  features: SiteMapFeatureCollection,
  locations: FacilityStorageLocation[],
): SiteMapFeatureCollection {
  const names = new Map(locations.map((location) => [location.locationID, location.name]));
  return {
    ...features,
    features: features.features.map((feature) => {
      const name = feature.properties.storageLocationID ? names.get(feature.properties.storageLocationID) : undefined;
      return name === undefined ? feature : { ...feature, properties: { ...feature.properties, label: name } };
    }),
  };
}
